import { inject, Injectable, isDevMode } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil } from 'rxjs';
import { clearAllState, getHealthSummary } from '../../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../../user/store/selectors/user.selectors';
import { PatientAllergiesService } from '../../services/patient-allergies/patient-allergies.service';
import {
  getAllPatientAllergies,
  getAllPatientAllergiesFailure,
  getAllPatientAllergiesSuccess,
  getPatientAllergies,
  getPatientAllergiesFailure,
  getPatientAllergiesSuccess,
  recordPatientAllergyPrintEvent,
  recordPatientAllergyPrintEventFailure,
  recordPatientAllergyPrintEventSuccess,
  setPageNumber,
} from '../actions/patient-allergy.actions';
import {
  selectCacheLoadedAt,
  selectCachePatientId,
  selectCurrentCount,
  selectIsFullDatasetLoaded,
  selectIsAtEndOfData,
  selectPageNumber,
  selectPageSize,
  selectPatientAllergies,
  selectTotalRecordCount,
} from '../selectors/patient-allergy.selector';

@Injectable()
export class PatientAllergyEffects {
  private readonly HEALTH_SUMMARY_WIDGET_PAGE_SIZE = 5;
  private readonly FULL_DATASET_CACHE_TTL_MS = 5 * 60 * 1000;
  private actions$ = inject(Actions);
  private patientAllergiesService = inject(PatientAllergiesService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getPatientAllergies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientAllergies, setPageNumber, getHealthSummary),
      concatLatestFrom(() => [
        this.store.select(selectPatientId),
        this.store.select(selectPageNumber),
        this.store.select(selectPageSize),
        this.store.select(selectCurrentCount),
        this.store.select(selectIsFullDatasetLoaded),
        this.store.select(selectCachePatientId),
        this.store.select(selectCacheLoadedAt),
        this.store.select(selectIsAtEndOfData),
        this.store.select(selectPatientAllergies),
        this.store.select(selectTotalRecordCount),
      ]),
      mergeMap(
        ([
          action,
          patientId,
          pageNumber,
          pageSize,
          currentCount,
          isFullDatasetLoaded,
          cachePatientId,
          cacheLoadedAt,
          isAtEndOfData,
          cachedPatientAllergies,
          totalRecordCount,
        ]) => {
          const shouldUseHealthSummaryCache = action.type === getHealthSummary.type && currentCount > 0;
          const requestPageSize =
            action.type === getHealthSummary.type ? this.HEALTH_SUMMARY_WIDGET_PAGE_SIZE : pageSize;

          if (shouldUseHealthSummaryCache) {
            if (isDevMode()) {
              console.debug('[Allergies][Cache] Health summary cache hit; skipping allergies API call.', {
                patientId,
                currentCount,
              });
            }
            return of(
              getPatientAllergiesSuccess({
                patientAllergies: cachedPatientAllergies,
                pageNumber: pageNumber || 1,
                isAtEndOfData: isAtEndOfData ?? false,
                totalRecordCount: totalRecordCount || currentCount,
              })
            );
          }

          const shouldUseFullDatasetCache =
            patientId != null &&
            isFullDatasetLoaded &&
            cachePatientId === patientId &&
            this.isCacheFresh(cacheLoadedAt);

          if (shouldUseFullDatasetCache) {
            if (isDevMode()) {
              console.debug('[Allergies][Cache] Full-dataset cache hit; skipping paged allergies API call.', {
                patientId,
                currentCount,
              });
            }
            return of(
              getPatientAllergiesSuccess({
                patientAllergies: cachedPatientAllergies,
                pageNumber: 1,
                isAtEndOfData: true,
                totalRecordCount: currentCount,
              })
            );
          }

          return this.patientAllergiesService.postPatientAllergies(patientId, pageNumber, requestPageSize).pipe(
            takeUntil(this.cancelRequest$),
            map(response => {
              let isAtEndOfData = false;
              const totalRecordCount = response.totalRecordCount;
              let currentPage = pageNumber || 1;
              let patientAllergies = response.result;

              if (patientAllergies == null || patientAllergies.length == 0) {
                patientAllergies = [];
                currentPage--;
                isAtEndOfData = true;
              } else if (currentCount + patientAllergies.length === totalRecordCount) {
                isAtEndOfData = true;
              }

              return getPatientAllergiesSuccess({
                patientAllergies,
                pageNumber: currentPage <= 0 ? 1 : currentPage,
                isAtEndOfData,
                totalRecordCount,
              });
            }),
            catchError(err => {
              return of(getPatientAllergiesFailure({ error: err }));
            })
          );
        }
      )
    );
  });

  getAllPatientAllergies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getAllPatientAllergies),
      concatLatestFrom(() => [
        this.store.select(selectIsFullDatasetLoaded),
        this.store.select(selectCachePatientId),
        this.store.select(selectCacheLoadedAt),
        this.store.select(selectPatientAllergies),
      ]),
      mergeMap(([{ patientId }, isFullDatasetLoaded, cachePatientId, cacheLoadedAt, patientAllergies]) => {
        if (isFullDatasetLoaded && cachePatientId === patientId && this.isCacheFresh(cacheLoadedAt)) {
          if (isDevMode()) {
            console.debug('[Allergies][Cache] Full-dataset cache hit; skipping GetAllByPatientId call.', {
              patientId,
              count: patientAllergies.length,
            });
          }
          return of(
            getAllPatientAllergiesSuccess({
              patientAllergies,
              patientId,
              loadedAt: cacheLoadedAt ?? Date.now(),
            })
          );
        }

        return this.patientAllergiesService.getAllPatientAllergiesByPatientId(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(patientAllergies =>
            getAllPatientAllergiesSuccess({
              patientAllergies,
              patientId,
              loadedAt: Date.now(),
            })
          ),
          catchError(err => of(getAllPatientAllergiesFailure({ error: err })))
        );
      })
    );
  });

  recordPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(recordPatientAllergyPrintEvent),
      mergeMap(({ patientId, patientAllergyIds }) =>
        this.patientAllergiesService.postPrintEvent(patientId, patientAllergyIds).pipe(
          map(() => recordPatientAllergyPrintEventSuccess()),
          catchError(err => of(recordPatientAllergyPrintEventFailure({ error: err })))
        )
      )
    );
  });

  private isCacheFresh(cacheLoadedAt: number | null): boolean {
    if (!cacheLoadedAt) {
      return false;
    }

    return Date.now() - cacheLoadedAt <= this.FULL_DATASET_CACHE_TTL_MS;
  }
}
